import React, { FC, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTheme } from '@/context/theme.context';
import { Text } from '@/components/atoms/AppText';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { tid } from '@/utils/testId';
import { DATE_TIME_PICKER_TEST_IDS as TID } from '@/constant/testIds/dateTimePicker.testIds';
import {
  APP_BG,
  APP_BORDER,
  APP_SEMANTIC,
  APP_HEX,
} from '@/constant/theme/colors.constant';

interface CalendarViewProps {
  currentMonth: Dayjs;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  hoveredDate: Dayjs | null;
  minDate?: Date;
  maxDate?: Date;
  onMonthChange: (month: Dayjs) => void;
  onDateClick: (date: Dayjs) => void;
  onDateHover: (date: Dayjs | null) => void;
  timezoneDetails: {
    currentTimezone: string;
    timezoneAbbreviation: string;
    currentTime: string;
  };
}

export const CalendarView: FC<CalendarViewProps> = ({
  currentMonth,
  startDate,
  endDate,
  hoveredDate,
  minDate,
  maxDate,
  onMonthChange,
  onDateClick,
  onDateHover,
  timezoneDetails,
}) => {
  // State to track mouse position for the tooltip
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const { colors } = useTheme();

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const renderToolTipOnCalender = () => {
    if (!startDate || endDate || !hoveredDate) return null;

    const isBefore = hoveredDate.isBefore(startDate, 'day');
    const dayDiff = Math.abs(hoveredDate.diff(startDate, 'day')) + 1;

    return (
      <div
        className="fixed z-[9999] pointer-events-none select-none flex items-center"
        style={{
          left: mousePos.x,
          top: mousePos.y,
          transform: 'translate(12px, -50%)',
        }}
      >
        {/* DOT */}
        <div className="relative flex items-center justify-center mr-2">
          <div
            className={`h-2 w-2 rounded-full z-10 ${isBefore ? 'bg-slate-400' : 'bg-primary'}`}
          />
          <div
            className={`absolute h-4 w-4 rounded-full animate-ping opacity-40 ${isBefore ? 'bg-slate-400' : 'bg-primary'}`}
          />
          <div
            className={`h-[1px] w-4 opacity-30 ${isBefore ? 'bg-slate-400' : 'bg-primary'}`}
          />
        </div>

        {/* MAIN BODY */}
        <div
          className={`
                flex items-center h-14 overflow-hidden
                rounded-2xl border
                shadow-[0_12px_30px_-10px_rgba(0,0,0,0.25)]
                bg-white/90 ${APP_BG.canvas} backdrop-blur-xl
                animate-in fade-in zoom-in-95 slide-in-from-left-3 duration-300
                ${
                  isBefore
                    ? 'border-slate-200 dark:border-slate-700'
                    : 'border-primary/20 dark:border-white/10'
                }
            `}
        >
          {/* BIG NUMBER */}
          <div className="flex items-center px-4 h-full bg-slate-50/50 dark:bg-white/5 translate-y-[1px]">
            <Text
              as="span"
              size="8xl"
              weight="black"
              color="inherit"
              leading="none"
              className={`tracking-tighter italic ${isBefore ? 'text-slate-400' : 'text-primary'}`}
            >
              {dayDiff}
            </Text>

            <div className="flex flex-col ml-1.5 mt-2 leading-[0.9] opacity-50 justify-center">
              <Text
                as="span"
                size="xs"
                weight="black"
                color="muted"
                tracking="tight"
                leading="none"
                className="uppercase"
              >
                {ORGANISM_PROPS.CALENDAR_VIEW.DAYS_LABEL}
              </Text>
              <Text
                as="span"
                size="xs"
                weight="black"
                color="muted"
                tracking="tight"
                leading="none"
                className="uppercase"
              >
                {ORGANISM_PROPS.CALENDAR_VIEW.TOTAL_LABEL}
              </Text>
            </div>
          </div>

          {/* DIVIDER */}
          <div className="h-7 w-px bg-slate-200 dark:bg-white/10" />

          {/* DATE SECTION */}
          <div className="flex flex-col justify-center px-4 min-w-[110px] leading-[1.05]">
            <Text
              as="span"
              size="2xs"
              weight="black"
              color="subtle"
              tracking="widest"
              className="uppercase mb-[2px]"
            >
              {isBefore
                ? ORGANISM_PROPS.CALENDAR_VIEW.NEW_START
                : ORGANISM_PROPS.CALENDAR_VIEW.RANGE_END}
            </Text>

            <div className="flex items-baseline gap-1">
              <Text
                as="span"
                size="xl"
                weight="bold"
                color="default"
                className="whitespace-nowrap"
              >
                {hoveredDate.format('MMM DD')}
              </Text>
              <Text as="span" size="sm" color="muted" className="opacity-50">
                {hoveredDate.format('ddd')}
              </Text>
            </div>
          </div>

          {/* END STRIP */}
          <div className="relative w-1.5 h-full overflow-hidden">
            <div
              className={`
                            absolute inset-0 animate-pulse
                            ${isBefore ? 'bg-slate-400' : 'bg-primary'}
                        `}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderCalendar = () => {
    const tz = timezoneDetails.currentTimezone;
    const startOfMonth = currentMonth.tz(tz).startOf('month');
    const startDay = startOfMonth.day();
    const daysInMonth = currentMonth.daysInMonth();
    const today = dayjs().tz(tz);

    const days = [];

    // Empty slots
    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-10 h-10" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = currentMonth.tz(tz).date(day).startOf('day');

      const start = startDate?.tz(tz).startOf('day');
      const end = endDate?.tz(tz).startOf('day');
      const hover = hoveredDate?.tz(tz).startOf('day');

      const isStart = start && date.isSame(start, 'day');
      const isEnd = end && date.isSame(end, 'day');

      const isInRange =
        start && end && date.isAfter(start, 'day') && date.isBefore(end, 'day');

      const isFuture = date.isAfter(today, 'day');

      const isDisabled =
        isFuture ||
        (minDate &&
          date.isBefore(dayjs(minDate).tz(tz).startOf('day'), 'day')) ||
        (maxDate && date.isAfter(dayjs(maxDate).tz(tz).startOf('day'), 'day'));

      const isToday = date.isSame(today, 'day');

      // Hover logic
      const isHoverForward =
        !end && start && hover && hover.isAfter(start, 'day');
      const isHoverBackward =
        !end && start && hover && hover.isBefore(start, 'day');

      const isHoverPreviewInRange =
        !end &&
        start &&
        hover &&
        ((date.isAfter(start, 'day') && date.isBefore(hover, 'day')) ||
          (date.isBefore(start, 'day') && date.isAfter(hover, 'day')));

      const isHoverVisualStart =
        !end &&
        start &&
        hover &&
        ((isHoverForward && date.isSame(start, 'day')) ||
          (isHoverBackward && date.isSame(hover, 'day')));

      const isHoverVisualEnd =
        !end &&
        start &&
        hover &&
        ((isHoverForward && date.isSame(hover, 'day')) ||
          (isHoverBackward && date.isSame(start, 'day')));

      const isSingleDate =
        start && end && start.isSame(end, 'day') && date.isSame(start, 'day');

      const dayOfWeek = date.day();
      const isRowStart = dayOfWeek === 0;
      const isRowEnd = dayOfWeek === 6;

      const inSelectedRange = isStart || isEnd || isInRange;
      const inHoverRange =
        isHoverPreviewInRange || isHoverVisualStart || isHoverVisualEnd;
      const inAnyRange = inSelectedRange || inHoverRange;

      const isVisualRangeStart = end
        ? isStart
        : hover
          ? isHoverVisualStart
          : isStart;

      const isVisualRangeEnd = end ? isEnd : hover ? isHoverVisualEnd : isEnd;

      // ✅ FINAL DARK-SAFE BACKGROUND
      let rangeBg = '';
      let rangeBgEndpointStyle: React.CSSProperties = {};
      if ((isVisualRangeStart || isVisualRangeEnd) && inSelectedRange) {
        rangeBgEndpointStyle = {
          background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
        };
      } else if ((isVisualRangeStart || isVisualRangeEnd) && inHoverRange) {
        rangeBg = 'bg-primary/20 dark:bg-primary/30';
      } else if (inSelectedRange) {
        rangeBg = 'bg-primary/15 dark:bg-white/10';
      } else if (inHoverRange) {
        rangeBg = 'bg-primary/10 dark:bg-white/5';
      }

      days.push(
        <div key={day} className="relative">
          {/* RANGE BG */}
          {inAnyRange && !isSingleDate && (
            <div
              className={`
                            absolute inset-0 h-10 z-0 ${rangeBg}
                            ${isVisualRangeStart && !isVisualRangeEnd ? 'rounded-l-2xl' : ''}
                            ${isVisualRangeEnd && !isVisualRangeStart ? 'rounded-r-2xl' : ''}
                            ${!isVisualRangeStart && !isVisualRangeEnd && isRowStart ? 'rounded-l-2xl' : ''}
                            ${!isVisualRangeStart && !isVisualRangeEnd && isRowEnd ? 'rounded-r-2xl' : ''}
                        `}
              style={rangeBgEndpointStyle}
            />
          )}

          {/* DAY BUTTON */}
          <button
            type="button"
            data-testid={tid(TID.CALENDAR_DAY_BTN(date.format('YYYY-MM-DD')))}
            disabled={isDisabled}
            onClick={() => !isDisabled && onDateClick(date)}
            onMouseEnter={() => !isDisabled && onDateHover(date)}
            onMouseLeave={() => onDateHover(null)}
            style={
              isSingleDate ||
              ((isStart || isEnd) && !isSingleDate) ||
              ((isHoverVisualStart || isHoverVisualEnd) && !isSingleDate)
                ? {
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                    color: APP_HEX.white,
                    borderRadius: '9999px',
                  }
                : {}
            }
            className={`
                        relative w-10 h-10 flex items-center justify-center text-sm font-semibold
                        transition-colors duration-150

                        ${isDisabled ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed' : 'cursor-pointer'}

                        ${isSingleDate ? 'text-white rounded-full z-10' : ''}

                        ${(isStart || isEnd) && !isSingleDate ? 'text-white font-bold rounded-full z-10' : ''}

                        ${(isHoverVisualStart || isHoverVisualEnd) && !isSingleDate ? 'text-white font-bold rounded-full z-10' : ''}

                        ${inSelectedRange && !isStart && !isEnd ? 'text-primary dark:text-white z-0' : ''}

                        ${inHoverRange && !isStart && !isEnd ? 'text-primary/70 dark:text-white/80 z-0' : ''}

                        ${isToday && !inAnyRange ? 'ring-2 ring-primary ring-inset' : ''}

                        ${
                          !inAnyRange && !isDisabled
                            ? 'text-slate-700 dark:text-slate-200 hover:bg-primary/10 dark:hover:bg-white/10 rounded-lg'
                            : ''
                        }
                    `}
          >
            {day}
          </button>
        </div>,
      );
    }

    return days;
  };

  return (
    <div
      className={`p-3 sm:p-4 flex flex-col justify-between w-full sm:w-80 max-w-full relative ${APP_BG.surface}`}
      onMouseMove={handleMouseMove}
      data-testid={tid(TID.CALENDAR_VIEW)}
    >
      {/* TOOLTIP */}
      {renderToolTipOnCalender()}

      <div>
        {/* HEADER */}
        <div className="flex items-center justify-between mb-3 px-1">
          <button
            type="button"
            data-testid={tid(TID.CALENDAR_PREV_BTN)}
            onClick={() => onMonthChange(currentMonth.subtract(1, 'month'))}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4 text-primary" />
          </button>

          <Text
            as="span"
            size="base"
            weight="bold"
            color="default"
            tracking="tight"
          >
            {currentMonth.format('MMMM YYYY')}
          </Text>

          <button
            type="button"
            data-testid={tid(TID.CALENDAR_NEXT_BTN)}
            onClick={() => onMonthChange(currentMonth.add(1, 'month'))}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
          >
            <ChevronRightIcon className="w-4 h-4 text-primary" />
          </button>
        </div>

        {/* WEEKDAYS */}
        <div className="grid grid-cols-7 mb-2">
          {ORGANISM_PROPS.CALENDAR_VIEW.WEEKDAYS.map((day) => (
            <Text
              key={day}
              as="span"
              size="sm"
              weight="semibold"
              color="subtle"
              tracking="wide"
              className="h-7 flex items-center justify-center uppercase"
            >
              {day}
            </Text>
          ))}
        </div>

        {/* CALENDAR GRID */}
        <div className="grid grid-cols-7 gap-y-1">{renderCalendar()}</div>
      </div>
    </div>
  );
};
